<template>
	<a-modal
		class="g-image-feature-card-edit-modal"
		:visible="visible"
		title="模块编辑"
		width="700px"
		:centered="true"
		:mask-closable="false"
		:destroy-on-close="true"
		@cancel="onCancel"
	>
		<div class="g-edit-modal__scroll">
			<div
				ref="editGrid"
				class="g-image-feature-card-edit"
				@click="clearSelection"
			>
				<div
					class="g-image-feature-card-edit__card-wrap"
					@click.stop="selectCard"
				>
					<div
						class="g-image-feature-card-edit__card"
						:class="{ 'is-selected': selected }"
						:style="cardStyle"
					>
						<div
							class="g-image-feature-card-edit__cover"
							:style="coverStyle"
						>
							<UploadProgressPanel
								v-if="isUploading"
								:tasks="uploadTasks"
								variant="item"
								hide-filename
							/>
							<template v-else>
								<img v-if="imageUrl" :src="imageUrl" alt="" />
								<i v-else class="mbm-iconfont mbm-tupian"></i>
							</template>
						</div>
						<div class="g-image-feature-card-edit__body">
							<div class="g-image-feature-card-edit__title-row">
								<input
									v-model="form.title"
									type="text"
									class="g-image-feature-card-edit__title-input"
									:style="{ color: form.titleColor }"
									maxlength="40"
									placeholder="请输入标题"
									@focus="selectCard"
								/>
								<i class="mbm-iconfont mbm-youjiantou g-image-feature-card-edit__arrow"></i>
							</div>
							<textarea
								v-model="form.description"
								class="g-image-feature-card-edit__desc-input"
								:style="{ color: form.descColor }"
								rows="3"
								maxlength="120"
								placeholder="请输入描述"
								@focus="selectCard"
							/>
						</div>
					</div>

					<div
						v-if="selected"
						class="g-image-feature-card-edit__panel g-image-feature-card-edit__panel--arrow g-image-feature-card-edit__panel--float is-align-center"
						@click.stop
					>
						<div class="g-image-feature-card-edit__link-row">
							<i class="mbm-iconfont mbm-lianjie"></i>
							<span :class="{ 'is-empty': !jumpLinkLabel }">{{ jumpLinkLabel || '未设置链接' }}</span>
						</div>
						<div class="g-image-feature-card-edit__toolbar">
							<button
								type="button"
								class="g-image-feature-card-edit__action"
								@click="jumpLinkVisible = true"
							>换链接</button>
							<button
								type="button"
								class="g-image-feature-card-edit__action"
								@click="triggerImageSelect"
							>换图</button>
							<span class="g-image-feature-card-edit__action-wrap">
								<button
									type="button"
									class="g-image-feature-card-edit__action"
									:class="{ 'is-active': activePanel === 'radius' }"
									@click="togglePanel('radius')"
								>圆角</button>
								<div
									v-if="activePanel === 'radius'"
									class="g-image-feature-card-edit__sub-panel"
								>
									<span class="g-image-feature-card-edit__sub-label">圆角 (px)</span>
									<a-input-number
										v-model:value="radiusDraft"
										:min="0"
										:max="40"
										:precision="0"
										class="g-image-feature-card-edit__sub-input"
									/>
									<button
										type="button"
										class="g-image-feature-card-edit__sub-confirm"
										@click="applyRadius"
									>确定</button>
								</div>
							</span>
							<div class="g-image-feature-card-edit__colors">
								<ColorPicker
									v-model="form.titleColor"
									title="标题颜色"
									@change="onTitleColorInput"
								/>
								<ColorPicker
									v-model="form.descColor"
									title="描述颜色"
									@change="onDescColorInput"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<input
			ref="fileInput"
			type="file"
			accept="image/*"
			class="g-image-feature-card-edit__file-input"
			@change="onFileChange"
		/>

		<JumpLink
			:visible="jumpLinkVisible"
			:jumpLink="form.jumpLink || emptyJumpLink"
			@submit="onJumpLinkSubmit"
			@cancel="jumpLinkVisible = false"
		/>

		<template #footer>
			<a-button @click="onCancel">取消</a-button>
			<a-button type="primary" class="g-image-feature-card-edit-modal__ok" :disabled="isUploading" @click="onConfirm">确认</a-button>
		</template>
	</a-modal>
</template>

<script>
	import JumpLink from '@/components/jumpLink.vue';
	import ColorPicker from './ColorPicker.vue';
	import UploadProgressPanel from './UploadProgressPanel.vue';
	import { isLocalImageFile, MAX_LOCAL_IMAGE_SIZE } from '../utils/localImage.js';
	import { createEmptyJumpLink, formatJumpLinkLabel } from '../utils/jumpLink.js';
	import { normalizeHexColor } from '../utils/richText.js';
	import { ensureEditFloatPanelVisible } from '../utils/editPanelAlign.js';
	import {
		createUploadTasks,
		updateUploadTaskProgress,
		uploadHeaderMediaFiles,
	} from '../utils/uploadHeaderMedia.js';
	import {
		getImageFeatureCardBlockStyle,
		getImageFeatureCardImageUrl,
		IMAGE_FEATURE_CARD_DEFAULT_RADIUS,
		normalizeImageFeatureCardData,
		replaceImageFeatureCardImage,
	} from '../utils/imageFeatureCard.js';

	export default {
		name: 'GImageFeatureCardEditModal',
		components: { JumpLink, ColorPicker, UploadProgressPanel },
		props: {
			visible: {
				type: Boolean,
				default: false,
			},
			data: {
				type: Object,
				default: () => ({}),
			},
		},
		emits: ['update:visible', 'confirm'],
		data() {
			return {
				form: normalizeImageFeatureCardData({}),
				selected: false,
				activePanel: '',
				radiusDraft: IMAGE_FEATURE_CARD_DEFAULT_RADIUS,
				jumpLinkVisible: false,
				emptyJumpLink: createEmptyJumpLink(),
				isUploading: false,
				uploadTasks: [],
			};
		},
		computed: {
			jumpLinkLabel() {
				return formatJumpLinkLabel(this.form.jumpLink);
			},
			imageUrl() {
				return getImageFeatureCardImageUrl(this.form.image);
			},
			blockStyle() {
				return getImageFeatureCardBlockStyle(this.form);
			},
			cardStyle() {
				return {
					borderRadius: this.blockStyle.borderRadius,
				};
			},
			coverStyle() {
				return {
					aspectRatio: this.blockStyle.coverAspectRatio,
				};
			},
		},
		watch: {
			visible(val) {
				if (val) {
					this.selected = false;
					this.activePanel = '';
					this.jumpLinkVisible = false;
					this.syncForm();
				}
			},
			data: {
				deep: true,
				handler() {
					if (this.visible) {
						this.syncForm();
					}
				},
			},
		},
		methods: {
			syncForm() {
				this.form = normalizeImageFeatureCardData(this.data);
				this.radiusDraft = this.form.borderRadius;
			},
			ensurePanelVisible() {
				if (!this.selected) return;
				// 与 ImageCollageEditModal 一致：需传入 selectedIndex，且 gridRef 必须是 wrap 的父节点
				ensureEditFloatPanelVisible(this, {
					gridRef: 'editGrid',
					wrapSelector: '.g-image-feature-card-edit__card-wrap',
					panelSelector: '.g-image-feature-card-edit__panel--float',
					selectedIndex: 0,
				});
			},
			selectCard() {
				this.selected = true;
				this.$nextTick(() => this.ensurePanelVisible());
			},
			clearSelection() {
				this.selected = false;
				this.activePanel = '';
			},
			togglePanel(panel) {
				this.activePanel = this.activePanel === panel ? '' : panel;
				if (panel === 'radius') {
					this.radiusDraft = this.form.borderRadius;
				}
				if (this.activePanel) {
					this.$nextTick(() => this.ensurePanelVisible());
				}
			},
			applyRadius() {
				this.form.borderRadius = Math.max(0, Number(this.radiusDraft) || 0);
				this.activePanel = '';
			},
			onTitleColorInput(color) {
				this.form.titleColor = normalizeHexColor(color);
			},
			onDescColorInput(color) {
				this.form.descColor = normalizeHexColor(color);
			},
			triggerImageSelect() {
				this.$refs.fileInput?.click();
			},
			async onFileChange(event) {
				const file = event.target.files?.[0];
				event.target.value = '';
				if (!file || !isLocalImageFile(file)) return;
				if (file.size > MAX_LOCAL_IMAGE_SIZE) {
					this.$message?.error?.('图片过大');
					return;
				}

				this.isUploading = true;
				this.uploadTasks = createUploadTasks([file]);
				try {
					const [item] = await uploadHeaderMediaFiles(this, {
						type: 'image',
						files: [file],
						onProgress: payload => updateUploadTaskProgress(this.uploadTasks, payload),
					});
					this.form.image = replaceImageFeatureCardImage(
						this.form.image,
						file,
						() => item,
					);
				} catch (error) {
					this.$message?.error?.('上传失败');
				} finally {
					this.isUploading = false;
					this.uploadTasks = [];
				}
			},
			onJumpLinkSubmit(link) {
				this.form.jumpLink = link?.type ? { ...link } : null;
				this.jumpLinkVisible = false;
			},
			onCancel() {
				this.$emit('update:visible', false);
			},
			onConfirm() {
				const payload = normalizeImageFeatureCardData({
					...this.form,
					title: this.form.title?.trim() || '请输入标题',
					description: this.form.description?.trim() || '',
				});
				this.$emit('confirm', payload);
				this.$emit('update:visible', false);
			},
		},
	};
</script>

<style scoped lang="less">
	@import '../styles/edit-float-panel.less';

	.edit-float-panel(g-image-feature-card-edit__panel);
	.edit-float-toolbar(g-image-feature-card-edit__toolbar);
	.edit-float-action(g-image-feature-card-edit__action);

	.g-image-feature-card-edit {
		padding: 8px 0 24px;
	}

	.g-image-feature-card-edit__card-wrap {
		position: relative;
		width: 100%;
		max-width: 620px;
		margin: 0 auto;
	}

	.g-image-feature-card-edit__card {
		overflow: hidden;
		background: #fff;
		box-shadow: 0 0 16px rgba(0, 0, 0, 0.1);
		border: 1px solid transparent;
		cursor: pointer;
		transition: border-color 0.2s, box-shadow 0.2s;

	}

	.g-image-feature-card-edit__cover {
		position: relative;
		width: 100%;
		overflow: hidden;
		background: #f0f0f0;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}

		i {
			font-size: 36px;
			color: #bfbfbf;
		}
	}

	.g-image-feature-card-edit__body {
		padding: 16px 18px 18px;
		box-sizing: border-box;
	}

	.g-image-feature-card-edit__title-row {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.g-image-feature-card-edit__title-input {
		flex: 1;
		min-width: 0;
		border: none;
		outline: none;
		background: transparent;
		padding: 0;
		font-size: 17px;
		font-weight: 700;
		line-height: 1.4;
	}

	.g-image-feature-card-edit__arrow {
		flex-shrink: 0;
		font-size: 14px;
		color: #c0c0c0;
	}

	.g-image-feature-card-edit__desc-input {
		display: block;
		width: 100%;
		margin-top: 8px;
		border: none;
		outline: none;
		resize: none;
		background: transparent;
		padding: 0;
		font-size: 13px;
		line-height: 1.6;
		font-family: inherit;
	}

	.g-image-feature-card-edit__panel {
		min-width: 280px;
	}

	.g-image-feature-card-edit__link-row {
		display: flex;
		align-items: center;
		gap: 8px;
		min-height: 40px;
		padding: 0 14px;
		border-bottom: 1px solid #f0f0f0;
		font-size: 13px;
		color: #333;
		white-space: nowrap;

		i {
			color: #999;
			font-size: 14px;
		}

		.is-empty {
			color: #999;
		}
	}

	.g-image-feature-card-edit__action-wrap {
		position: relative;
		display: inline-flex;
	}

	.g-image-feature-card-edit__colors {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		margin-left: 8px;
	}

	.g-image-feature-card-edit__sub-panel {
		position: absolute;
		top: calc(100% + 8px);
		left: 50%;
		transform: translateX(-50%);
		z-index: 2;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 10px 12px;
		background: #fff;
		border: 1px solid #eceef2;
		border-radius: 8px;
		box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
		white-space: nowrap;
	}

	.g-image-feature-card-edit__sub-label {
		font-size: 12px;
		color: #666;
	}

	.g-image-feature-card-edit__sub-input {
		width: 80px;
	}

	.g-image-feature-card-edit__sub-confirm {
		height: 28px;
		padding: 0 10px;
		border: none;
		border-radius: 4px;
		background: #285af9;
		color: #fff;
		font-size: 12px;
		cursor: pointer;
	}

	.g-image-feature-card-edit__file-input {
		display: none;
	}
</style>

<style lang="less">
	@import '../styles/edit-float-panel.less';

	.g-image-feature-card-edit-modal {
		.edit-modal-body();
		.edit-modal-scroll();

		.g-image-feature-card-edit-modal__ok {
			min-width: 72px;
		}
	}
</style>
